import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import PostHeader from './PostHeader';
import PostCaption from './PostCaption';
import PostImageDetailToggler from './PostImageDetailToggler';

/**
 * This is supposed to be a publically viewable page,
 * at least for events that are not private, so this is
 * why we see a lot of checks like "if (props.currentUser)..."
 */

const MIN_DETAIL_HEIGHT_PX = 290;

const Post = (props) => {
  const { id, post, attendees, likers, currUserLiked, currUserAttending } = props;
  const [detailViewOpen, toggleDetailView] = useState(false);
  const [ dimensions, setDimensions ] = useState({ width: 0, height: MIN_DETAIL_HEIGHT_PX });

  const postImageRef = useRef(null);

  const navigate = useNavigate();

  const handleToggleDetails = () => {
    toggleDetailView(!detailViewOpen);
  };

  // get additional post data only if the session is authenticated
  useEffect(() => {
    if (props.currentUser) {
      props.init(props.id);
    }
  }, [props.currentUser, props.id]);

  // Measure image once it loads
  useEffect(() => {
    const updateDimensions = () => {
      if (postImageRef.current) {
        setDimensions({
          width: postImageRef.current.offsetWidth,
          height: postImageRef.current.offsetHeight
        });
      }
    };

    // Run once on mount
    updateDimensions();

    // Add load event listener to handle dynamic loading
    const imageElement = postImageRef.current;
    if (imageElement) {
      imageElement.addEventListener('load', updateDimensions);
      return () => imageElement.removeEventListener('load', updateDimensions);
    }
  }, []);

  // Higher-order function to handle authenticated actions
  const withAuth = (action) => (...args) => {
    if (!props.currentUser) {
      navigate(`/start?postref=${id}`);
      return;
    }
    return action(...args);
  };

  const updateStar = withAuth(newVal => (
    props.updatePostStar(props.id, props.currentUser.id, newVal)
  ));

  const updateAttend = withAuth(newVal => (
    props.updateAttendance(props.id, props.currentUser.id, newVal)
  ));

  const updateFollow = withAuth(newVal => (
    props.updateFollow(props.post.username, newVal)
  ));

  return (
    <div className="post-container">
      <PostHeader
        postId={id}
        username={post.username}
        postTitle={post.title}
        postTimestamp={post.eventTimestamp}
        toggleDetailView={handleToggleDetails}
      />
      <PostImageDetailToggler
        post={post}
        postImageRef={postImageRef}
        attendees={attendees}
        userIsLoggedIn={!!props.currentUser}
        userIsAuthor={props.currentUser && props.currentUser.username === props.post.username || false}
        postIsStarred={currUserLiked}
        userIsAttending={currUserAttending}
        updateStar={val => updateStar(val)}
        updateAttending={val => updateAttend(val)}
        updateFollow={val => updateFollow(val)}
        imageHeightPx={dimensions.height}
        detailViewOpen={detailViewOpen}
        toggleDetailView={handleToggleDetails}
        saves={likers}
        numComments={props.numComments}
      />
      {/* text={post.description} */}
      <PostCaption
        username={post.username}
      />
    </div>
  );
};

Post.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.number,
    token: PropTypes.string,
    username: PropTypes.string,
  }),

  post: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    location: PropTypes.string,
    username: PropTypes.string,
    description: PropTypes.string,
    isPrivate: PropTypes.bool,
    isAccessible: PropTypes.bool,
    ageRestriction: PropTypes.string,
    eventTimestamp: PropTypes.string,
    publicUrl: PropTypes.string,
    thumbPublicUrl: PropTypes.string,
  }).isRequired,

  attendees: PropTypes.arrayOf(
    PropTypes.shape({
      username: PropTypes.string,
    }),
  ).isRequired,

  likers: PropTypes.arrayOf(
    PropTypes.shape({
      username: PropTypes.string,
    }),
  ).isRequired,

  id: PropTypes.number.isRequired,
  currUserAttending: PropTypes.bool,
  currUserLiked: PropTypes.bool,
  numComments: PropTypes.number.isRequired,

  updatePostStar: PropTypes.func.isRequired,
  updateAttendance: PropTypes.func.isRequired,
  updateFollow: PropTypes.func.isRequired,
  init: PropTypes.func.isRequired,
};

Post.defaultProps = {
  currUserAttending: false,
  currUserLiked: false,
  currentUser: null,
};

export default Post;
